import React, { useState, useRef, useCallback } from "react";
import axios from "axios";
import {
  FiFileText,
  FiUpload,
  FiX,
  FiCheckCircle,
  FiAlertCircle,
  FiTrash2,
  FiUploadCloud,
} from "react-icons/fi";
import { webServiceUrl } from "./apiCall/webserviceurl";

const MAX_SIZE_MB = 5;
const ACCEPTED_TYPES = [".pdf", ".doc", ".docx", ".jpg", ".jpeg", ".png"];

// Update this to your actual API base URL if needed (e.g. via env var)
const UPLOAD_URL = "/uploadReliving";

export default function RelievingLetterUpload({
  onClose = () => {},
  onSubmit = () => {},
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [file, setFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState("");
  const [status, setStatus] = useState("idle"); // idle | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [uploadedData, setUploadedData] = useState(null);
  const inputRef = useRef(null);
  const abortControllerRef = useRef(null);

  const validateFile = (f) => {
    const ext = "." + f.name.split(".").pop().toLowerCase();
    if (!ACCEPTED_TYPES.includes(ext)) {
      return "Unsupported file type. Upload a PDF, Word doc, or image.";
    }
    if (f.size > MAX_SIZE_MB * 1024 * 1024) {
      return `File is too large. Maximum size is ${MAX_SIZE_MB}MB.`;
    }
    return "";
  };

  const uploadFile = async (f) => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const formData = new FormData();
    formData.append("relieving-letter", f);

    const token = localStorage.getItem("token");

    try {
      const response = await axios.post(
        webServiceUrl.uploadReliving,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              const pct = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              setProgress(pct);
            }
          },
        },
      );

      setUploadedData(response.data);
      setStatus("done");
      setProgress(100);
    } catch (err) {
      if (axios.isCancel(err)) {
        // Upload was cancelled (e.g. file removed mid-upload); nothing to show
        return;
      }
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Upload failed. Please try again.";
      setError(message);
      setStatus("idle");
      setFile(null);
      setProgress(0);
    }
  };

  const handleFile = useCallback((f) => {
    if (!f) return;
    const err = validateFile(f);
    if (err) {
      setError(err);
      setFile(null);
      setStatus("idle");
      return;
    }
    setError("");
    setFile(f);
    setStatus("uploading");
    setProgress(0);
    uploadFile(f);
  }, []);

  const onDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const f = e.dataTransfer.files?.[0];
    handleFile(f);
  };

  const removeFile = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setFile(null);
    setStatus("idle");
    setProgress(0);
    setError("");
    setUploadedData(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const close = () => {
    setIsOpen(false);
    onClose();
  };

  const submit = () => {
    if (status !== "done") return;
    onSubmit(uploadedData ?? file);
    close();
  };

  if (!isOpen) return null;

  return (
    <div
      className="rlu-overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <style>{CSS}</style>

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="relieving-letter-title"
        className="rlu-modal"
      >
        {/* Header */}
        <div className="rlu-header">
          <div className="rlu-header-left">
            <div className="rlu-header-icon">
              <FiFileText size={20} color="#ffffff" />
            </div>
            <div>
              <h2 id="relieving-letter-title" className="rlu-title">
                Upload relieving letter
              </h2>
              <p className="rlu-subtitle">From your previous employer</p>
            </div>
          </div>
          <button onClick={close} aria-label="Close" className="rlu-close-btn">
            <FiX size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="rlu-body">
          {!file && (
            <div
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={onDrop}
              onClick={() => inputRef.current?.click()}
              className={`rlu-dropzone${isDragging ? " rlu-dropzone-active" : ""}`}
            >
              <div className="rlu-upload-circle">
                <FiUpload size={22} color="#16a34a" />
              </div>
              <p className="rlu-drop-title">Drag and drop your file here</p>
              <p className="rlu-drop-sub">
                or{" "}
                <span className="rlu-browse-link">browse from your device</span>
              </p>
              <p className="rlu-drop-hint">
                PDF, DOC, DOCX, JPG or PNG — up to {MAX_SIZE_MB}MB
              </p>
              <input
                ref={inputRef}
                type="file"
                className="rlu-hidden-input"
                accept={ACCEPTED_TYPES.join(",")}
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </div>
          )}

          {error && (
            <div className="rlu-error-box">
              <FiAlertCircle
                size={16}
                color="#16a34a"
                className="rlu-error-icon"
              />
              <p className="rlu-error-text">{error}</p>
            </div>
          )}

          {file && (
            <div className="rlu-file-card">
              <div className="rlu-file-row">
                <div className="rlu-file-icon">
                  {status === "done" ? (
                    <FiCheckCircle size={19} color="#16a34a" />
                  ) : (
                    <FiUploadCloud size={18} color="#16a34a" />
                  )}
                </div>
                <div className="rlu-file-info">
                  <p className="rlu-file-name">{file.name}</p>
                  <p className="rlu-file-meta">
                    {(file.size / 1024).toFixed(0)} KB ·{" "}
                    {status === "done" ? "Ready to submit" : "Uploading…"}
                  </p>
                </div>
                <button
                  onClick={removeFile}
                  aria-label="Remove file"
                  className="rlu-remove-btn"
                >
                  <FiTrash2 size={16} />
                </button>
              </div>

              {status === "uploading" && (
                <div className="rlu-progress-track">
                  <div
                    className="rlu-progress-fill"
                    style={{ width: `${progress}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="rlu-footer">
          <button onClick={close} className="rlu-btn rlu-btn-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={status !== "done"}
            className={`rlu-btn rlu-btn-submit${status === "done" ? " rlu-btn-submit-active" : ""}`}
          >
            Submit letter
          </button>
        </div>
      </div>
    </div>
  );
}

const CSS = `
/* =========================================================
   RELIEVING LETTER UPLOAD — MODERN IHFC UI
   ========================================================= */

.rlu-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.62);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-sizing: border-box;
  animation: rlu-fade-in 0.2s ease-out;
}

.rlu-modal {
  width: 100%;
  max-width: 500px;
  max-height: calc(100vh - 40px);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 20px;

  box-shadow:
    0 25px 50px -12px rgba(15, 23, 42, 0.25),
    0 8px 24px rgba(15, 23, 42, 0.08);

  box-sizing: border-box;
  animation: rlu-modal-in 0.25s ease-out;
}


/* =========================================================
   HEADER
   ========================================================= */

.rlu-header {
  position: relative;
  padding: 22px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  background:
    linear-gradient(
      135deg,
      #15803d 0%,
      #16a34a 55%,
      #22c55e 100%
    );

  flex-shrink: 0;
}

.rlu-header::after {
  content: "";
  position: absolute;
  width: 140px;
  height: 140px;
  right: -55px;
  top: -85px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  pointer-events: none;
}

.rlu-header-left {
  position: relative;
  z-index: 1;

  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
}

.rlu-header-icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.22);

  box-shadow: inset 0 1px 1px rgba(255, 255, 255, 0.12);
}

.rlu-title {
  margin: 0;

  color: #ffffff;
  font-size: 17px;
  font-weight: 650;
  line-height: 1.3;
  letter-spacing: -0.015em;
}

.rlu-subtitle {
  margin: 3px 0 0;

  color: rgba(255, 255, 255, 0.78);
  font-size: 12.5px;
  font-weight: 400;
  line-height: 1.4;
}

.rlu-close-btn {
  position: relative;
  z-index: 2;

  width: 34px;
  height: 34px;
  padding: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 9px;

  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.08);

  cursor: pointer;

  transition:
    background 0.18s ease,
    transform 0.18s ease;
}

.rlu-close-btn:hover {
  background: rgba(255, 255, 255, 0.18);
  transform: scale(1.04);
}

.rlu-close-btn:active {
  transform: scale(0.96);
}


/* =========================================================
   BODY
   ========================================================= */

.rlu-body {
  padding: 24px;
  overflow-y: auto;
  background: #ffffff;
  box-sizing: border-box;
}


/* =========================================================
   DROPZONE
   ========================================================= */

.rlu-dropzone {
  position: relative;

  min-height: 235px;
  padding: 32px 24px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  text-align: center;

  border: 1.5px dashed #cbd5e1;
  border-radius: 16px;

  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f8fafc 100%
    );

  cursor: pointer;

  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease;

  box-sizing: border-box;
}

.rlu-dropzone:hover {
  border-color: #22a052;
  background:
    linear-gradient(
      180deg,
      #ffffff 0%,
      #f0fdf4 100%
    );

  box-shadow:
    0 8px 24px rgba(22, 163, 74, 0.07);
}

.rlu-dropzone:active {
  transform: scale(0.995);
}

.rlu-dropzone-active {
  border-color: #16a34a !important;
  border-style: solid;

  background: #f0fdf4 !important;

  box-shadow:
    0 0 0 4px rgba(22, 163, 74, 0.08),
    0 10px 30px rgba(22, 163, 74, 0.08);
}

.rlu-upload-circle {
  width: 58px;
  height: 58px;
  margin-bottom: 16px;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 50%;

  background: #ecfdf3;
  border: 1px solid #d1fae5;

  box-shadow:
    0 4px 12px rgba(22, 163, 74, 0.08);

  transition:
    transform 0.2s ease,
    background 0.2s ease;
}

.rlu-dropzone:hover .rlu-upload-circle {
  transform: translateY(-2px);
  background: #dcfce7;
}

.rlu-drop-title {
  margin: 0;

  color: #17201b;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
}

.rlu-drop-sub {
  margin: 6px 0 0;

  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.rlu-browse-link {
  color: #15803d;
  font-weight: 600;
  text-decoration: none;
  border-bottom: 1px solid rgba(21, 128, 61, 0.35);

  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.rlu-browse-link:hover {
  color: #166534;
  border-color: #166534;
}

.rlu-drop-hint {
  margin: 16px 0 0;
  padding: 6px 10px;

  color: #64748b;
  font-size: 11.5px;
  line-height: 1.4;

  background: #f1f5f9;
  border-radius: 6px;
}

.rlu-hidden-input {
  display: none;
}


/* =========================================================
   ERROR
   ========================================================= */

.rlu-error-box {
  display: flex;
  align-items: flex-start;
  gap: 9px;

  margin-top: 14px;
  padding: 11px 13px;

  border: 1px solid #fecaca;
  border-radius: 10px;

  background: #fef2f2;

  box-sizing: border-box;
}

.rlu-error-icon {
  margin-top: 1px;
  flex-shrink: 0;

  color: #dc2626 !important;
}

.rlu-error-text {
  margin: 0;

  color: #b91c1c;
  font-size: 12.5px;
  font-weight: 500;
  line-height: 1.45;
}


/* =========================================================
   FILE CARD
   ========================================================= */

.rlu-file-card {
  padding: 16px;

  border: 1px solid #dbe5de;
  border-radius: 14px;

  background: #f8faf9;

  box-sizing: border-box;

  animation: rlu-slide-up 0.2s ease-out;
}

.rlu-file-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.rlu-file-icon {
  width: 42px;
  height: 42px;
  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 10px;

  background: #ecfdf3;
  border: 1px solid #d1fae5;
}

.rlu-file-info {
  min-width: 0;
  flex: 1;
}

.rlu-file-name {
  margin: 0;

  color: #17201b;
  font-size: 13.5px;
  font-weight: 600;
  line-height: 1.35;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rlu-file-meta {
  margin: 3px 0 0;

  color: #64748b;
  font-size: 11.5px;
  line-height: 1.4;
}

.rlu-remove-btn {
  width: 32px;
  height: 32px;
  padding: 0;

  flex-shrink: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  border: 1px solid #e2e8f0;
  border-radius: 8px;

  color: #64748b;
  background: #ffffff;

  cursor: pointer;

  transition:
    color 0.15s ease,
    background 0.15s ease,
    border-color 0.15s ease;
}

.rlu-remove-btn:hover {
  color: #dc2626;
  background: #fef2f2;
  border-color: #fecaca;
}


/* =========================================================
   PROGRESS
   ========================================================= */

.rlu-progress-track {
  position: relative;

  height: 6px;
  margin-top: 14px;

  overflow: hidden;

  border-radius: 999px;

  background: #e2e8f0;
}

.rlu-progress-fill {
  height: 100%;

  border-radius: inherit;

  background:
    linear-gradient(
      90deg,
      #16a34a,
      #22c55e
    );

  transition: width 0.2s ease;
}


/* =========================================================
   FOOTER
   ========================================================= */

.rlu-footer {
  min-height: 72px;
  padding: 14px 24px;

  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;

  border-top: 1px solid #eef2f0;

  background: #ffffff;

  flex-shrink: 0;
  box-sizing: border-box;
}

.rlu-btn {
  min-width: 104px;

  padding: 10px 18px;

  border-radius: 9px;

  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.4;

  cursor: pointer;

  transition:
    background 0.18s ease,
    border-color 0.18s ease,
    color 0.18s ease,
    box-shadow 0.18s ease,
    transform 0.12s ease;
}

.rlu-btn:active {
  transform: scale(0.98);
}

.rlu-btn:focus-visible,
.rlu-close-btn:focus-visible,
.rlu-remove-btn:focus-visible,
.rlu-dropzone:focus-visible {
  outline: 3px solid rgba(22, 163, 74, 0.2);
  outline-offset: 2px;
}


/* Cancel */

.rlu-btn-cancel {
  color: #475569;
  background: #ffffff;
  border: 1px solid #d7dee5;
}

.rlu-btn-cancel:hover {
  color: #1e293b;
  background: #f8fafc;
  border-color: #cbd5e1;
}


/* Submit */

.rlu-btn-submit {
  color: #ffffff;
  background: #d1d5db;
  border: 1px solid #d1d5db;
  cursor: not-allowed;
}

.rlu-btn-submit-active {
  color: #ffffff;
  background: #16a34a;
  border-color: #16a34a;
  cursor: pointer;

  box-shadow:
    0 3px 8px rgba(22, 163, 74, 0.18);
}

.rlu-btn-submit-active:hover {
  background: #15803d;
  border-color: #15803d;

  box-shadow:
    0 5px 12px rgba(22, 163, 74, 0.24);
}


/* =========================================================
   ANIMATIONS
   ========================================================= */

@keyframes rlu-fade-in {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@keyframes rlu-modal-in {
  from {
    opacity: 0;
    transform: translateY(8px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes rlu-slide-up {
  from {
    opacity: 0;
    transform: translateY(5px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}


/* =========================================================
   MOBILE
   ========================================================= */

@media (max-width: 480px) {
  .rlu-overlay {
    padding: 0;
    align-items: flex-end;
  }

  .rlu-modal {
    width: 100%;
    max-width: 100%;
    max-height: 94vh;

    border-radius: 20px 20px 0 0;

    animation: rlu-mobile-in 0.25s ease-out;
  }

  .rlu-header {
    padding: 18px 18px;
  }

  .rlu-header-icon {
    width: 38px;
    height: 38px;
  }

  .rlu-title {
    font-size: 15.5px;
  }

  .rlu-subtitle {
    font-size: 12px;
  }

  .rlu-body {
    padding: 18px;
  }

  .rlu-dropzone {
    min-height: 220px;
    padding: 28px 16px;
  }

  .rlu-upload-circle {
    width: 52px;
    height: 52px;
    margin-bottom: 14px;
  }

  .rlu-drop-title {
    font-size: 14px;
  }

  .rlu-drop-sub {
    font-size: 12.5px;
  }

  .rlu-drop-hint {
    font-size: 10.5px;
  }

  .rlu-file-card {
    padding: 13px;
  }

  .rlu-footer {
    min-height: auto;
    padding: 13px 18px;
    padding-bottom: calc(
      13px + env(safe-area-inset-bottom, 0px)
    );

    flex-direction: column-reverse;
    gap: 8px;
  }

  .rlu-btn {
    width: 100%;
    min-width: 0;
    padding: 11px 18px;
  }
}

@media (max-width: 340px) {
  .rlu-title {
    font-size: 14.5px;
  }

  .rlu-dropzone {
    min-height: 205px;
  }

  .rlu-drop-title {
    font-size: 13px;
  }
}


/* =========================================================
   REDUCED MOTION
   ========================================================= */

@media (prefers-reduced-motion: reduce) {
  .rlu-overlay,
  .rlu-modal,
  .rlu-file-card {
    animation: none;
  }

  .rlu-dropzone,
  .rlu-upload-circle,
  .rlu-btn,
  .rlu-close-btn,
  .rlu-remove-btn {
    transition: none;
  }
}
`;


// const CSS = `
// .rlu-overlay {
//   position: fixed;
//   inset: 0;
//   z-index: 50;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   padding: 16px;
//   background: rgba(15, 23, 32, 0.55);
//   backdrop-filter: blur(4px);
//   box-sizing: border-box;
// }

// .rlu-modal {
//   width: 100%;
//   max-width: 448px;
//   max-height: calc(100vh - 32px);
//   border-radius: 18px;
//   background: #ffffff;
//   box-shadow: 0 24px 60px rgba(22, 163, 74, 0.28);
//   border: 1px solid #16a34a;
//   overflow: hidden;
//   display: flex;
//   flex-direction: column;
//   box-sizing: border-box;
// }

// .rlu-header {
//   padding: 22px 24px 18px;
//   background: #16a34a;
//   flex-shrink: 0;
//   display: flex;
//   align-items: flex-start;
//   justify-content: space-between;
//   gap: 12px;
// }

// .rlu-header-left {
//   display: flex;
//   align-items: center;
//   gap: 12px;
//   min-width: 0;
// }

// .rlu-header-icon {
//   width: 40px;
//   height: 40px;
//   flex-shrink: 0;
//   border-radius: 12px;
//   background: rgba(255, 255, 255, 0.18);
//   display: flex;
//   align-items: center;
//   justify-content: center;
// }

// .rlu-title {
//   margin: 0;
//   color: #ffffff;
//   font-weight: 600;
//   font-size: 17px;
//   letter-spacing: -0.01em;
//   line-height: 1.25;
// }

// .rlu-subtitle {
//   margin: 2px 0 0;
//   color: rgba(255, 255, 255, 0.85);
//   font-size: 13px;
// }

// .rlu-close-btn {
//   border: none;
//   background: transparent;
//   color: #ffffff;
//   border-radius: 8px;
//   padding: 6px;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   cursor: pointer;
//   flex-shrink: 0;
//   transition: background 0.15s ease;
// }
// .rlu-close-btn:hover {
//   background: rgba(255, 255, 255, 0.18);
// }

// .rlu-body {
//   padding: 20px 24px;
//   overflow-y: auto;
// }

// .rlu-dropzone {
//   display: flex;
//   flex-direction: column;
//   align-items: center;
//   justify-content: center;
//   text-align: center;
//   border-radius: 14px;
//   cursor: pointer;
//   padding: 36px 20px;
//   border: 1.5px dashed #818381;
//   background: #ffffff;
//   transition: border-color 0.15s ease, background 0.15s ease;
// }
// .rlu-dropzone-active {
//   border-color: #16a34a;
//   background: rgba(22, 163, 74, 0.08);
// }

// .rlu-upload-circle {
//   width: 48px;
//   height: 48px;
//   border-radius: 50%;
//   background: rgba(22, 163, 74, 0.12);
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   margin-bottom: 12px;
// }

// .rlu-drop-title {
//   margin: 0;
//   font-size: 14.5px;
//   color: #000000;
//   font-weight: 500;
// }

// .rlu-drop-sub {
//   margin: 4px 0 0;
//   font-size: 13px;
//   color: rgba(93, 95, 94, 0.65);
// }

// .rlu-browse-link {
//   color: #272727;
//   font-weight: 600;
//   text-decoration: underline;
// }

// .rlu-drop-hint {
//   margin: 14px 0 0;
//   font-size: 11.5px;
//   color: rgba(22, 163, 74, 0.55);
// }

// .rlu-hidden-input {
//   display: none;
// }

// .rlu-error-box {
//   display: flex;
//   align-items: flex-start;
//   gap: 8px;
//   border-radius: 10px;
//   margin-top: 12px;
//   padding: 10px 12px;
//   background: rgba(22, 163, 74, 0.08);
//   border: 1px solid #16a34a;
//   box-sizing: border-box;
// }

// .rlu-error-icon {
//   margin-top: 1px;
//   flex-shrink: 0;
// }

// .rlu-error-text {
//   margin: 0;
//   font-size: 13px;
//   color: #16a34a;
//   font-weight: 500;
// }

// .rlu-file-card {
//   border-radius: 14px;
//   padding: 14px 16px;
//   border: 1px solid #16a34a;
//   background: #ffffff;
//   box-sizing: border-box;
// }

// .rlu-file-row {
//   display: flex;
//   align-items: center;
//   gap: 12px;
// }

// .rlu-file-icon {
//   width: 38px;
//   height: 38px;
//   border-radius: 10px;
//   flex-shrink: 0;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   background: rgba(22, 163, 74, 0.12);
// }

// .rlu-file-info {
//   min-width: 0;
//   flex: 1;
// }

// .rlu-file-name {
//   margin: 0;
//   font-size: 13.5px;
//   color: #16a34a;
//   font-weight: 500;
//   white-space: nowrap;
//   overflow: hidden;
//   text-overflow: ellipsis;
// }

// .rlu-file-meta {
//   margin: 1px 0 0;
//   font-size: 12px;
//   color: rgba(22, 163, 74, 0.65);
// }

// .rlu-remove-btn {
//   border: none;
//   background: transparent;
//   color: #16a34a;
//   padding: 6px;
//   border-radius: 8px;
//   flex-shrink: 0;
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   cursor: pointer;
//   transition: background 0.15s ease, color 0.15s ease;
// }
// .rlu-remove-btn:hover {
//   background: rgba(22, 163, 74, 0.12);
// }

// .rlu-progress-track {
//   margin-top: 12px;
//   height: 5px;
//   border-radius: 999px;
//   background: rgba(22, 163, 74, 0.15);
//   overflow: hidden;
// }

// .rlu-progress-fill {
//   height: 100%;
//   background: #16a34a;
//   transition: width 0.18s ease;
// }

// .rlu-footer {
//   display: flex;
//   align-items: center;
//   justify-content: flex-end;
//   gap: 10px;
//   padding: 16px 24px;
//   border-top: 1px solid rgba(22, 163, 74, 0.2);
//   background: #ffffff;
//   flex-shrink: 0;
// }

// .rlu-btn {
//   border: none;
//   border-radius: 10px;
//   font-weight: 500;
//   font-size: 13.5px;
//   padding: 10px 18px;
//   cursor: pointer;
//   transition: background 0.15s ease;
// }

// .rlu-btn-cancel {
//   color: #16a34a;
//   background: #ffffff;
//   border: 1px solid #16a34a;
// }
// .rlu-btn-cancel:hover {
//   background: rgba(22, 163, 74, 0.08);
// }

// .rlu-btn-submit {
//   color: #ffffff;
//   background: rgba(22, 163, 74, 0.4);
//   cursor: not-allowed;
// }
// .rlu-btn-submit-active {
//   background: #16a34a;
//   cursor: pointer;
// }
// .rlu-btn-submit-active:hover {
//   background: #128a3e;
// }

// /* ---------- Mobile responsiveness ---------- */
// @media (max-width: 480px) {
//   .rlu-overlay {
//     padding: 0;
//     align-items: flex-end;
//   }

//   .rlu-modal {
//     max-width: 100%;
//     width: 100%;
//     max-height: 92vh;
//     border-radius: 20px 20px 0 0;
//   }

//   .rlu-header {
//     padding: 18px 18px 16px;
//   }

//   .rlu-header-icon {
//     width: 36px;
//     height: 36px;
//   }

//   .rlu-title {
//     font-size: 15.5px;
//   }

//   .rlu-subtitle {
//     font-size: 12px;
//   }

//   .rlu-body {
//     padding: 16px 18px;
//   }

//   .rlu-dropzone {
//     padding: 28px 14px;
//   }

//   .rlu-upload-circle {
//     width: 42px;
//     height: 42px;
//   }

//   .rlu-drop-title {
//     font-size: 13.5px;
//   }

//   .rlu-drop-sub {
//     font-size: 12px;
//   }

//   .rlu-drop-hint {
//     font-size: 11px;
//   }

//   .rlu-file-card {
//     padding: 12px 14px;
//   }

//   .rlu-file-name {
//     font-size: 13px;
//   }

//   .rlu-footer {
//     padding: 14px 18px calc(14px + env(safe-area-inset-bottom, 0px));
//     flex-direction: column-reverse;
//     gap: 8px;
//   }

//   .rlu-btn {
//     width: 100%;
//     padding: 12px 18px;
//     text-align: center;
//   }
// }

// @media (max-width: 340px) {
//   .rlu-title {
//     font-size: 14.5px;
//   }

//   .rlu-drop-title {
//     font-size: 13px;
//   }
// }
// `;
